import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { t } from 'i18next'
import api from '@/core/services/api'
import { useCommonState } from '@/core/context/useContext'
import { G } from './theme'

/**
 * 접속 시 알림 창 「お知らせ」 — gold8.bet .announcementDialog 모양.
 *   데이터: /site/popup 중 관리자(jp-admin 매장 > 팝업)에서 등록한 알림(no ≥ 1)만.
 *     - 「팝업 노출」(popup_expo_yn) = Y 인 것만 띄운다.
 *     - 비로그인 상태에서는 「비로그인 노출」(guest_view_yn) = Y 인 것만.
 *     - 노출 기간은 API 가 이미 거른다.
 *     이벤트 팝업(no 0 으로 내려오는 행)은 넣지 않는다 — 이벤트 화면에서 따로 다룬다.
 *   PC: 1000×633, 노란 머리, 왼쪽 27% 제목 목록 · 오른쪽 내용, 위쪽 「二度と表示しない」.
 *   모바일: 화면 폭 창, 위 제목 목록(2건 이상일 때) · 내용 · 「わかりました」.
 *   「二度と表示しない」는 브라우저에 기억한다 — 제목·내용을 고치면 다시 뜬다.
 */
const HIDE_KEY = 'goldNoticeHide'

const sig = (p) => {
  const s = `${p.no}|${p.title}|${p.contents}`
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return `${p.no}:${h}`
}

const readHidden = () => {
  try { return JSON.parse(localStorage.getItem(HIDE_KEY) || '[]') } catch (e) { return [] }
}

const writeHidden = (list) => {
  try { localStorage.setItem(HIDE_KEY, JSON.stringify(list.slice(-50))) } catch (e) { /* 저장 불가(사생활 보호 모드 등) — 이번 방문만 닫힘 */ }
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 990;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: ${({ $mobile }) => ($mobile ? '70px' : '110px')};
  background: rgba(0, 0, 0, 0.6);
  overflow-y: auto;
`

const Dialog = styled.div`
  position: relative;
  width: ${({ $mobile }) => ($mobile ? 'calc(100% - 24px)' : '1000px')};
  max-width: calc(100% - 24px);
  padding-top: 26px;
`

const NoShow = styled.label`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  color: #fff;
  cursor: pointer;
  input { margin: 0; }
`

const Head = styled.div`
  position: relative;
  min-height: 42px;
  padding: 7px 44px;
  border-radius: 8px 8px 0 0;
  background: ${G.gold};
  text-align: center;
  font-size: 18px;
  font-weight: 700;
  line-height: 28px;
  color: #1a1200;
`

const Close = styled.button`
  position: absolute;
  top: 7px;
  right: 18px;
  padding: 0;
  border: 0;
  background: none;
  font-size: 22px;
  line-height: 28px;
  color: #1a1200;
  cursor: pointer;
`

const Body = styled.div`
  display: flex;
  flex-direction: ${({ $mobile }) => ($mobile ? 'column' : 'row')};
  height: ${({ $mobile }) => ($mobile ? 'auto' : '560px')};
  background: #fff;
`

const Menu = styled.ul`
  flex: 0 0 ${({ $mobile }) => ($mobile ? 'auto' : '27%')};
  height: ${({ $mobile }) => ($mobile ? 'auto' : '100%')};
  margin: 0;
  padding: 0;
  list-style: none;
  overflow-y: auto;
  border-right: ${({ $mobile }) => ($mobile ? '0' : '1px solid #d6d6d6')};
  border-bottom: ${({ $mobile }) => ($mobile ? '1px solid #d6d6d6' : '0')};
`

const MenuItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 50px;
  padding: 0 12px;
  border-bottom: 1px solid #ccc;
  background: ${({ $on }) => ($on ? G.gold : '#fff')};
  font-size: 14px;
  color: #000;
  cursor: pointer;
  &:last-child { border-bottom: 0; }
  span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #000; }
  svg { flex: 0 0 auto; width: 16px; height: 16px; fill: currentColor; color: #555; }
`

const Content = styled.div`
  flex: 1;
  height: ${({ $mobile }) => ($mobile ? 'auto' : '100%')};
  max-height: ${({ $mobile }) => ($mobile ? '55vh' : 'none')};
  padding: 20px 24px;
  overflow-y: auto;
  font-size: 14px;
  line-height: 1.7;
  color: #222;
  word-break: break-word;
  img { max-width: 100%; height: auto; vertical-align: top; }
  p { margin: 0 0 6px; }
  * { color: inherit; }
`

const Foot = styled.div`
  display: flex;
  justify-content: center;
  padding: 14px 0 16px;
  background: #fff;
  border-top: 1px solid #e3e3e3;
  border-radius: 0 0 8px 8px;
`

const OkBtn = styled.button`
  min-width: 140px;
  height: 42px;
  border: 0;
  border-radius: 6px;
  background: ${G.gold};
  font-size: 15px;
  font-weight: 700;
  color: #1a1200;
  cursor: pointer;
`

export default function GoldNoticePopup({ mobile = false }) {
  const { isLogin } = useCommonState()
  const [items, setItems] = useState([])
  const [active, setActive] = useState(0)
  const [noShow, setNoShow] = useState(false)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    api.get('/site/popup')
      .then((res) => {
        const all = res.data?.content?.data || []
        const hidden = readHidden()
        const list = all.filter(p =>
          Number(p.no) > 0 &&
          p.popup_expo_yn === 'Y' &&
          (isLogin || p.guest_view_yn === 'Y') &&
          !hidden.includes(sig(p)))
        setItems(list)
        setActive(0)
        setOpen(list.length > 0)
      })
      .catch(console.error)
  }, [isLogin])

  const cur = items[active]
  const html = useMemo(() => ({ __html: cur?.contents || '' }), [cur])

  const close = () => {
    if (noShow) writeHidden([...readHidden(), ...items.map(sig)])
    setOpen(false)
  }

  if (!open || !cur) return null

  return (
    <Overlay $mobile={mobile} onClick={(e) => { if (e.target === e.currentTarget) close() }}>
      <Dialog $mobile={mobile} role="dialog" aria-modal="true">
        <NoShow>
          <input type="checkbox" checked={noShow} onChange={(e) => setNoShow(e.target.checked)} />
          {t('gold.noShowAgain')}
        </NoShow>
        <Head>
          {t('gold.notice')}
          <Close type="button" aria-label="close" onClick={close}>×</Close>
        </Head>
        <Body $mobile={mobile}>
          {(!mobile || items.length > 1) && (
            <Menu $mobile={mobile}>
              {items.map((p, i) => (
                <MenuItem key={p.no} $on={i === active} onClick={() => setActive(i)}>
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-5 4v-4H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" /></svg>
                  <span>{p.title}</span>
                </MenuItem>
              ))}
            </Menu>
          )}
          <Content $mobile={mobile} dangerouslySetInnerHTML={html} />
        </Body>
        {mobile && (
          <Foot>
            <OkBtn type="button" onClick={close}>{t('gold.understood')}</OkBtn>
          </Foot>
        )}
      </Dialog>
    </Overlay>
  )
}
